import { useState, useEffect, useRef, useCallback } from 'react'

/* ── Markdown renderer ───────────────────────────────────────────────────── */
function inlineFormat (text) {
  return text
    .replace(
      /\*\*(.+?)\*\*/g,
      '<strong style="color:var(--ink);font-weight:600">$1</strong>'
    )
    .replace(/\*(.+?)\*/g, '<em style="color:var(--ink-3)">$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(
      /\(Source (\d+), Page (\d+)\)/g,
      '<span class="citation-chip">📄 p.$2</span>'
    )
}

function renderMarkdown (text) {
  if (!text) return null
  const lines = text.split('\n')
  const elements = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (line.startsWith('## ')) {
      elements.push(
        <h3
          key={i}
          style={{
            fontWeight: 700,
            fontSize: 14,
            margin: '10px 0 4px',
            color: 'var(--ink)'
          }}
        >
          {line.slice(3)}
        </h3>
      )
    } else if (line.startsWith('# ')) {
      elements.push(
        <h2
          key={i}
          style={{
            fontWeight: 700,
            fontSize: 16,
            margin: '12px 0 6px',
            color: 'var(--ink)'
          }}
        >
          {line.slice(2)}
        </h2>
      )
    } else if (line.startsWith('### ')) {
      elements.push(
        <h4
          key={i}
          style={{
            fontWeight: 600,
            fontSize: 13,
            margin: '8px 0 3px',
            color: 'var(--ink-2)'
          }}
        >
          {line.slice(4)}
        </h4>
      )
    } else if (
      line.startsWith('- ') ||
      line.startsWith('• ') ||
      line.startsWith('* ')
    ) {
      elements.push(
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 8,
            padding: '2px 0',
            alignItems: 'flex-start'
          }}
        >
          <span
            style={{
              color: 'var(--accent-2)',
              flexShrink: 0,
              marginTop: 1,
              fontSize: 15
            }}
          >
            ·
          </span>
          <span
            className='md-content'
            style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ink-2)' }}
            dangerouslySetInnerHTML={{ __html: inlineFormat(line.slice(2)) }}
          />
        </div>
      )
    } else if (line.match(/^\d+\.\s/)) {
      const content = line.replace(/^\d+\.\s/, '')
      const num = line.match(/^(\d+)/)[1]
      elements.push(
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 9,
            padding: '2px 0',
            alignItems: 'flex-start'
          }}
        >
          <span
            style={{
              color: 'white',
              background: 'var(--accent)',
              fontWeight: 700,
              flexShrink: 0,
              fontSize: 10,
              borderRadius: '50%',
              width: 17,
              height: 17,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 3
            }}
          >
            {num}
          </span>
          <span
            className='md-content'
            style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ink-2)' }}
            dangerouslySetInnerHTML={{ __html: inlineFormat(content) }}
          />
        </div>
      )
    } else if (line.trim() === '---') {
      elements.push(
        <hr
          key={i}
          style={{
            border: 'none',
            borderTop: '1px solid var(--border)',
            margin: '8px 0'
          }}
        />
      )
    } else if (
      line.startsWith('📌') ||
      line.startsWith('💡') ||
      line.startsWith('⚡') ||
      line.startsWith('🔑')
    ) {
      elements.push(
        <div
          key={i}
          style={{
            background: 'var(--accent-soft)',
            borderLeft: '3px solid var(--accent)',
            borderRadius: '0 var(--radius) var(--radius) 0',
            padding: '8px 12px',
            margin: '6px 0',
            fontSize: 13,
            lineHeight: 1.65
          }}
          dangerouslySetInnerHTML={{ __html: inlineFormat(line) }}
        />
      )
    } else if (line.trim()) {
      elements.push(
        <p
          key={i}
          style={{
            fontSize: 13,
            lineHeight: 1.75,
            color: 'var(--ink-2)',
            margin: '2px 0'
          }}
          dangerouslySetInnerHTML={{ __html: inlineFormat(line) }}
        />
      )
    } else {
      elements.push(<div key={i} style={{ height: 4 }} />)
    }
    i++
  }
  return elements
}

function CitationChips ({ citations }) {
  if (!citations?.length) return null
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 9 }}>
      {citations.slice(0, 5).map((c, i) => (
        <span key={i} className='citation-chip' title={c.excerpt}>
          <span className='material-symbols-rounded' style={{ fontSize: 11 }}>
            description
          </span>
          {c.source?.split('/').pop()?.split('\\').pop() || `Source ${c.index}`}
          {c.page !== '?' && ` · p.${c.page}`}
        </span>
      ))}
    </div>
  )
}

const textToMarkdown = text => {
  if (!text) return ''

  return text
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')

    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^##### (.+)$/gm, '<h5>$1</h5>')
    .replace(/^###### (.+)$/gm, '<h6>$1</h6>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^\* (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, m => `<ul>${m}</ul>`)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^---$/gm, '<hr>')
    .replace(/^(📌|💡|⚡|🔑)(.+)$/gm, '<div class="callout">$1$2</div>')
    .replace(/\n{2,}/g, '</p><p>')
    .replace(/^(?!<[hul]|<div|<hr)(.+)$/gm, '<p>$1</p>')
    .replace(/<p><\/p>/g, '')
}

function MessageBubble ({ msg }) {
  const isUser = msg.role === 'user'
  const isSocratic = msg.mode === 'socratic'

  if (isUser) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 14
        }}
      >
        <div className='chat-bubble-user' style={{ maxWidth: '76%' }}>
          <div
            dangerouslySetInnerHTML={{ __html: textToMarkdown(msg.content) }}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        marginBottom: 16,
        alignItems: 'flex-start'
      }}
      className='animate-fadeIn'
    >
      {/* AI avatar */}
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          flexShrink: 0,
          marginTop: 2,
          background: isSocratic
            ? 'var(--amber-soft)'
            : 'linear-gradient(135deg, var(--accent), var(--sky))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: isSocratic ? '2px solid var(--amber)' : 'none',
          boxShadow: isSocratic ? 'none' : '0 2px 8px var(--accent-glow)'
        }}
      >
        <span
          className='material-symbols-rounded'
          style={{
            fontSize: 15,
            color: isSocratic ? 'var(--amber)' : 'white',
            fontVariationSettings: "'FILL' 1"
          }}
        >
          {isSocratic ? 'psychology_alt' : 'smart_toy'}
        </span>
      </div>

      {/* Bubble */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {isSocratic && (
          <div style={{ marginBottom: 6 }}>
            <span className='badge badge-amber' style={{ fontSize: 10 }}>
              <span
                className='material-symbols-rounded'
                style={{ fontSize: 12 }}
              >
                psychology_alt
              </span>
              Socratic Mode
            </span>
          </div>
        )}
        <div className='chat-bubble-ai' style={{ overflow: 'hidden' }}>
          {msg.streaming && msg.content === '' ? (
            <div style={{ display: 'flex', gap: 5, padding: '3px 0' }}>
              <div className='typing-dot' />
              <div className='typing-dot' />
              <div className='typing-dot' />
            </div>
          ) : (
            <div className='md-content'>{renderMarkdown(msg.content)}</div>
          )}
          {msg.streaming && msg.content && (
            <span
              style={{
                display: 'inline-block',
                width: 2,
                height: 13,
                background: 'var(--accent)',
                marginLeft: 2,
                animation: 'pulse 0.8s infinite',
                verticalAlign: 'middle'
              }}
            />
          )}
          <CitationChips citations={msg.citations} />
        </div>
        {!msg.streaming && (
          <div
            style={{
              fontSize: 10.5,
              color: 'var(--ink-3)',
              marginTop: 3,
              paddingLeft: 2
            }}
          >
            {new Date().toLocaleTimeString('en-IN', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const QUICK_PROMPTS = [
  'Summarize the key concepts',
  'What are the most important formulas?',
  'Explain the hardest topic',
  'Give me 5 likely exam questions'
]

export default function ChatPanel ({
  notebook,
  messages,
  sending,
  tutorMode,
  socraticMode,
  onSend,
  onUpload,
  uploading
}) {
  const [draft, setDraft] = useState('')
  const bottomRef = useRef(null)
  const textRef = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, sending])

  const submitDraft = useCallback(() => {
    const text = draft.trim()
    if (!text || !notebook || sending) return
    setDraft('')
    if (textRef.current) textRef.current.style.height = 'auto'
    onSend(text)
  }, [draft, notebook, sending, onSend])

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submitDraft()
    }
  }

  const autoResize = e => {
    e.target.style.height = 'auto'
    e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px'
  }

  /* ── Empty state ── */
  if (!notebook) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 20,
          padding: 40,
          textAlign: 'center',
          background: 'var(--bg)'
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'var(--accent-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(99,102,241,0.2)'
          }}
        >
          <span
            className='material-symbols-rounded'
            style={{
              fontSize: 34,
              color: 'var(--accent-2)',
              fontVariationSettings: "'FILL' 1"
            }}
          >
            menu_book
          </span>
        </div>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            No notebook selected
          </h2>
          <p
            style={{
              color: 'var(--ink-3)',
              fontSize: 13,
              lineHeight: 1.6,
              maxWidth: 300
            }}
          >
            Import a PDF, Word doc, or image to create a notebook and start
            chatting.
          </p>
        </div>
        <button
          onClick={onUpload}
          disabled={uploading}
          className='btn btn-primary btn-lg'
        >
          <span
            className='material-symbols-rounded'
            style={{ fontSize: 18, fontVariationSettings: "'FILL' 1" }}
          >
            upload_file
          </span>
          {uploading ? 'Processing…' : 'Import Document'}
        </button>
      </div>
    )
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--bg)'
      }}
    >
      {/* Notebook topbar */}
      <div
        style={{
          padding: '10px 16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            background: 'var(--accent-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            border: '1px solid rgba(99,102,241,0.2)'
          }}
        >
          <span
            className='material-symbols-rounded'
            style={{
              fontSize: 17,
              color: 'var(--accent-2)',
              fontVariationSettings: "'FILL' 1"
            }}
          >
            menu_book
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {notebook.title}
          </div>
        </div>
        <div
          className='badge badge-accent'
          style={{ fontSize: 10.5, flexShrink: 0 }}
        >
          {socraticMode ? '🧠 Socratic' : `📚 ${tutorMode || 'Student'}`}
        </div>

        {/* Processing indicator in topbar */}
        {sending && (
          <div
            className='ai-indicator animate-fadeIn'
            style={{ fontSize: 11, flexShrink: 0 }}
          >
            <span
              className='material-symbols-rounded'
              style={{ fontSize: 13, fontVariationSettings: "'FILL' 1" }}
            >
              smart_toy
            </span>
            Thinking
            <div className='dots'>
              <div className='dot' />
              <div className='dot' />
              <div className='dot' />
            </div>
          </div>
        )}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 8px' }}>
        {messages.length === 0 && !sending && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 280,
              gap: 20,
              textAlign: 'center'
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background:
                  'linear-gradient(135deg, var(--accent), var(--sky))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px var(--accent-glow)'
              }}
            >
              <span
                className='material-symbols-rounded'
                style={{
                  fontSize: 28,
                  color: 'white',
                  fontVariationSettings: "'FILL' 1"
                }}
              >
                smart_toy
              </span>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>
                Ready to help
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--ink-3)',
                  maxWidth: 340,
                  lineHeight: 1.6
                }}
              >
                Ask anything about{' '}
                <strong style={{ color: 'var(--ink-2)' }}>
                  {notebook.title}
                </strong>
                . I'll cite sources and page numbers.
              </div>
            </div>

            {/* Quick prompts */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 7,
                justifyContent: 'center',
                maxWidth: 400
              }}
            >
              {QUICK_PROMPTS.map(q => (
                <button
                  key={q}
                  onClick={() => onSend(q)}
                  style={{
                    padding: '7px 13px',
                    borderRadius: 'var(--radius-full)',
                    border: '1px solid var(--border-2)',
                    background: 'var(--surface)',
                    color: 'var(--ink-2)',
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all var(--transition)'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = 'var(--accent)'
                    e.currentTarget.style.color = 'var(--accent-2)'
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'var(--border-2)'
                    e.currentTarget.style.color = 'var(--ink-2)'
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <MessageBubble key={i} msg={msg} />
        ))}

        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div
        style={{
          padding: '12px 16px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface)',
          flexShrink: 0
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-end',
            background: 'var(--surface-3)',
            border: '1px solid var(--border-2)',
            borderRadius: 'var(--radius-xl)',
            padding: '8px 10px 8px 14px',
            transition: 'border-color var(--transition)'
          }}
          onFocusCapture={e =>
            (e.currentTarget.style.borderColor = 'var(--border-focus)')
          }
          onBlurCapture={e =>
            (e.currentTarget.style.borderColor = 'var(--border-2)')
          }
        >
          <textarea
            ref={textRef}
            value={draft}
            onChange={e => {
              setDraft(e.target.value)
              autoResize(e)
            }}
            onKeyDown={handleKeyDown}
            placeholder={`Ask anything about ${notebook.title}…`}
            rows={1}
            disabled={sending}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              resize: 'none',
              outline: 'none',
              padding: 0,
              fontSize: 13,
              lineHeight: 1.5,
              color: 'var(--ink)',
              minHeight: 24,
              fontFamily: 'var(--font-sans)',
              maxHeight: 140,
              overflowY: 'auto'
            }}
          />
          <button
            onClick={submitDraft}
            disabled={!draft.trim() || sending}
            style={{
              width: 32,
              height: 32,
              borderRadius: 9,
              flexShrink: 0,
              background:
                draft.trim() && !sending ? 'var(--accent)' : 'var(--surface-2)',
              border: '1px solid',
              borderColor:
                draft.trim() && !sending ? 'var(--accent)' : 'var(--border-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all var(--transition)',
              cursor: draft.trim() && !sending ? 'pointer' : 'default',
              boxShadow:
                draft.trim() && !sending
                  ? '0 2px 8px var(--accent-glow)'
                  : 'none'
            }}
          >
            {sending ? (
              <div className='spinner spinner-sm' />
            ) : (
              <span
                className='material-symbols-rounded'
                style={{
                  fontSize: 17,
                  color: draft.trim() ? 'white' : 'var(--ink-3)',
                  fontVariationSettings: "'FILL' 1"
                }}
              >
                send
              </span>
            )}
          </button>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 6,
            fontSize: 10.5,
            color: 'var(--ink-3)',
            padding: '0 2px'
          }}
        >
          <span>Enter to send · Shift+Enter for newline</span>
          <span>{messages.filter(m => m.role === 'user').length} messages</span>
        </div>
      </div>
    </div>
  )
}
